/** @jsx isolateComponent */
import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { TestimonialSoloProps } from '../TestimonialSolo';
import {
  Paginator,
  OnPaginateData,
  Avatar,
  Colors,
  ScrollSnap,
  Typography,
} from '../../../index';
import {
  BackgroundColors,
  remapDeprecatedColors,
  breakpoints,
} from '../TestimonialSolo/TestimonialMapping';
import { css } from '@emotion/react';
import { debounce } from '../../../utils/debounce';

type TestimponialProps = TestimonialSoloProps & {
  mediaElement: React.ReactNode;
};

export type Props = {
  /**
   * Array of testimonial elements.
   * Fields:
   *
   * `quote`: Text content displayed on the top or below the avatar.
   *
   * `author`: Text content displayed below quote.
   *
   * `company`: Text content displayed below author initials.
   *
   * `hasAuthorAvatar`?: Whether or not Testimonial has author avatar. Default value is `False`.
   *
   * `avatarUrl`?: Author avatar url.
   *
   * `mediaElement`: an Image or a Video element that will be shown besides the Testimonial
   */
  testimonials: Array<Omit<TestimponialProps, 'backgroundColor'>>;
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava10' | 'ocean10' | 'sun10' | 'white' | 'cream' | 'neutral100' | 'acidWash' | 'neutral200'.
   */
  backgroundColor?: BackgroundColors;
};

const Styles = labelStyles('testimonialCarousel', {
  testimonialCarouselContainer: (
    backgroundColor: BackgroundColors = 'neutral100'
  ) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${Colors[remapDeprecatedColors(backgroundColor)]};
    padding: 20px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 40px 30px;
    }
  `,
  testimonialContainerWrapper: css`
    width: 100%;
    display: flex;
    overflow-x: auto;
    gap: 40px;
    // Fits inside our 1230px max grid, plus about 100px of padding on each side of the carousel
    // so that it is centered and not quite as wide as a Hero, for example
    max-width: 1030px;
    &::-webkit-scrollbar {
      /* Hide scrollbar for Chrome, Safari and Opera */
      display: none;
    }
    -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
    scrollbar-width: none; /* Hide scrollbar for Firefox */
    @media screen and (min-width: ${breakpoints.tablet}px) {
      gap: 60px;
    }
  `,
  testimonialContainer: css`
    min-width: 100%;
    display: grid;
    align-items: flex-start;
    grid-template-columns: repeat(1,1fr);
    gap: 30px 0;
    margin-bottom: 30px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      grid-template-columns: repeat(2,1fr);
      gap: 0 30px;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      margin-bottom: 40px;
      gap: 0 100px;
    }
  }
  `,
  testimonial: css`
    text-align: left;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      max-width: 339px;
    }
    @media screen and (min-width: ${breakpoints.desktop}px) {
      max-width: 400px;
    }
  `,
  avatarContainer: css`
    margin-bottom: 20px;
  `,
  quote: css`
    ${Typography.paragraph3}
    color: ${Colors.neutral800};
    margin-bottom: 20px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.subHeader1}
    }
  `,
  author: css`
    ${Typography.paragraph3}
    color: ${Colors.neutral700};
  `,
});

export const TestimonialCarousel = (props: Props) => {
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const testimonialContainerRef = useRef<HTMLDivElement>(null);
  const [testimonialContainerWidth, setTestimonialContainerWidth] = useState(0);

  useEffect(() => {
    // set initial width of the Testimonial Container
    setTestimonialContainerWidth(
      testimonialContainerRef?.current?.offsetWidth || 0
    );
  }, []);

  useLayoutEffect(() => {
    const onResize = debounce(() => {
      setTestimonialContainerWidth(
        testimonialContainerRef?.current?.offsetWidth || 0
      );
    }, 100);
    window.addEventListener('resize', onResize, { capture: true });
    return () => {
      window.removeEventListener('resize', onResize, { capture: true });
    };
  });

  const paginator = (
    <Paginator
      currentPageIndex={activeTestimonialIndex}
      onPaginate={({ pageIndex: index }: OnPaginateData) =>
        setActiveTestimonialIndex(index)
      }
      pageUrls={Array.from({ length: props.testimonials.length })}
    />
  );

  return (
    <div css={Styles.testimonialCarouselContainer(props.backgroundColor)}>
      <div
        css={Styles.testimonialContainerWrapper}
        ref={testimonialContainerRef}
      >
        {testimonialContainerWidth && (
          <ScrollSnap
            gap={60}
            itemWidth={testimonialContainerWidth}
            onScroll={(index: number) => {
              setActiveTestimonialIndex(index);
            }}
            pageIndex={activeTestimonialIndex}
            scrollSnapAlign="center"
          >
            {props.testimonials.map((testimonial, i) => (
              <div key={i} css={Styles.testimonialContainer}>
                <div>{testimonial.mediaElement}</div>
                <div css={Styles.testimonial}>
                  {testimonial.hasAuthorAvatar && (
                    <div css={Styles.avatarContainer}>
                      <Avatar
                        data-testId="Avatar"
                        name={testimonial.author}
                        url={testimonial.avatarUrl}
                        size={40}
                      />
                    </div>
                  )}
                  <div css={Styles.quote}>{testimonial.quote}</div>
                  <p css={Styles.author}>
                    {testimonial.author}, {testimonial.company}
                  </p>
                </div>
              </div>
            ))}
          </ScrollSnap>
        )}
      </div>
      {paginator}
    </div>
  );
};
